/// <reference types="react-scripts" />
import * as React from "react";
import * as taskService from "../../../models/services/taskService";
import { Base } from "ui/Scripts/source/framework/base";
import { ISavedChartPanel } from "../../../models/chart/savedChartPanel";
import { DashboardChart } from "source/components/framework/DashboardChart";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IDashboardChartData } from "source/models/report/dashboardChartData";
import { AppUtils } from "source/framework/appUtils";
import { getTaskGeneralFilterNotChanged, getFilterStateFromTaskGeneralFilter, ITaskGeneralFilter } from "./TaskPageUtils";

export interface ITaskPageDashboardChartProp {
    companyId: string;
    chartPanel: ISavedChartPanel;
    taskTypes: ITaskEditTypeItem[];
    onShowChartPanelPropertiesDialog: (panelId: string) => void;
}

export interface ITaskPageDashboardChartState extends ITaskGeneralFilter {
    isLoading: boolean;
    dashboardChartData: IDashboardChartData;
    reportId: string;
    chartId: string;
    chartHash: string;
}

export class TaskPageDashboardChart extends React.Component<ITaskPageDashboardChartProp, ITaskPageDashboardChartState> {
    _isMounted: boolean;

    static getTaskPageDashboardChartStateNotChanged = (prevState: ITaskPageDashboardChartState, state: ITaskPageDashboardChartState): boolean => {
        return prevState.reportId === state.reportId &&
            prevState.chartId === state.chartId &&
            prevState.chartHash === state.chartHash &&
            getTaskGeneralFilterNotChanged(prevState, state);
    };

    getStateChangeFromProps = (props: ITaskPageDashboardChartProp): any => {
        const reportFilterProps = getFilterStateFromTaskGeneralFilter(props.chartPanel?.generalFilter);
        let chartHash = "";
        const taskType = props.taskTypes.find(i => i.id === props.chartPanel.reportId);
        if (taskType) {
            const chart = taskType.charts.find(i => i.name === props.chartPanel.chartId);
            if (chart) {
                chartHash = JSON.stringify(chart);
            }
        }
        const result = {
            unitIds: reportFilterProps.selectedUnitIds ? reportFilterProps.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [],
            stateIds: reportFilterProps.selectedStateIds ? reportFilterProps.selectedStateIds.filter(i => i !== Base.emptyGuid) : [],
            priorityIds: reportFilterProps.selectedPriorityIds ? reportFilterProps.selectedPriorityIds.filter(i => i !== Base.emptyGuid) : [],
            taskTypeIds: reportFilterProps.selectedTaskTypeIds ? reportFilterProps.selectedTaskTypeIds.filter(i => i !== Base.emptyGuid) : [],
            handlerUserCodes: reportFilterProps.selectedHandlerUserCodes ? reportFilterProps.selectedHandlerUserCodes.filter(i => !!i) : [],
            personInChargeUserCodes: reportFilterProps.selectedPersonInChargeUserCodes ? reportFilterProps.selectedPersonInChargeUserCodes.filter(i => !!i) : [],
            taskTypeHeaderFields: reportFilterProps.selectedTaskTypeHeaderFields ?? "",
            taskTypeFields: reportFilterProps.selectedTaskTypeFields ?? "",
            reportId: props.chartPanel?.reportId ?? "",
            chartId: props.chartPanel?.chartId ?? "",
            chartHash
        };
        return result;
    };

    constructor(props: ITaskPageDashboardChartProp) {
        super(props);
        this.state = {
            ...this.getStateChangeFromProps(props),
            dashboardChartData: null,
            isLoading: true
        };
    }

    createChart = async () => {
        const props = this.props;
        const state = this.state;
        if (!props.chartPanel || !props.taskTypes || !props.taskTypes.length) return;
        const taskType = props.taskTypes.find(i => i.id === props.chartPanel.reportId);
        if (!taskType) return;
        const chart = taskType.charts.find(i => i.name === props.chartPanel.chartId);
        if (!chart) return;
        this.setState({ isLoading: true });
        const dashboardChartData = await AppUtils.callService(() => taskService.getChartData(props.companyId, props.chartPanel.chartId, props.chartPanel.id,
            state.unitIds, state.stateIds, state.priorityIds, state.taskTypeIds, state.handlerUserCodes, state.personInChargeUserCodes,
            state.taskTypeHeaderFields, state.taskTypeFields), false, true, () => { if (!this._isMounted) return; this.setState({ isLoading: false }); });
        if (!dashboardChartData || !this._isMounted) return;
        dashboardChartData.title = props.chartPanel.title;
        this.setState({
            dashboardChartData
        });
    };

    componentDidUpdate(prevProps: ITaskPageDashboardChartProp) {
        const state = this.state;
        const newState = this.getStateChangeFromProps(this.props);
        if (TaskPageDashboardChart.getTaskPageDashboardChartStateNotChanged(state, newState)) return;
        this.setState({
            ...newState
        }, this.createChart);
    }

    componentDidMount(): void {
        this.createChart();
        this._isMounted = true;
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }
    
    render() {
        const props = this.props;
        const state = this.state;
        const chartPanel = props.chartPanel;
        if (!chartPanel) return null;
        return (
            <DashboardChart
                panelId={chartPanel.id}
                title={chartPanel.getTitle()}
                chartData={state.dashboardChartData}
                isLoading={state.isLoading}
                onShowProperties={props.onShowChartPanelPropertiesDialog}
            />
        );
    }
}